import React, { createContext, useContext, useLayoutEffect, useRef, useState } from 'react';
import {
  ButtonBlock,
  CardsBlock,
  DividerBlock,
  HeadingBlock,
  LogosBlock,
  MediaTextBlock,
  QuoteBlock,
  StatsBlock,
  VideoBlock,
  CalloutBlock,
  CtaBlock,
  FaqBlock,
  FeatureGridBlock,
  GalleryBlock,
  HeroBlock,
  MediaBlock,
  RelatedBlock,
  RichTextBlock,
  SpecsBlock,
  StepsBlock,
  ChipListBlock,
  TableBlock,
  ToolDetailBlock
} from './blocks';
import { ContentSection, ToolContentDocument } from './document';
import { isImageUrl, resolveImageUrl } from './media';
import './ToolDetailRenderer.scss';


// ─── Icone inline (autonome: nessuna dipendenza dal framework ospite) ────────
// Path stroke-based su viewBox 24x24, ereditano colore da currentColor.
const ICON_PATHS: Record<string, React.ReactNode> = {
  radar: <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3.5" /><path d="M12 12l6-4" /></>,
  compare: <><rect x="3" y="5" width="8" height="14" rx="1.5" /><rect x="13" y="5" width="8" height="14" rx="1.5" /></>,
  clock: <><circle cx="12" cy="12" r="8" /><path d="M12 8v4l3 2" /></>,
  activity: <path d="M3 12h4l2 6 4-12 2 6h6" />,
  report: <><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M9 8h6M9 12h6M9 16h4" /></>,
  lock: <><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></>,
  shield: <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />,
  cloud: <path d="M7 18a4 4 0 0 1 0-8 5 5 0 0 1 9.5-1.5A3.5 3.5 0 0 1 17 18H7z" />,
  refresh: <><path d="M4 12a8 8 0 0 1 13.5-5.5L20 9" /><path d="M20 4v5h-5" /><path d="M20 12a8 8 0 0 1-13.5 5.5L4 15" /><path d="M4 20v-5h5" /></>,
  bolt: <path d="M13 3L5 13h5l-1 8 8-10h-5l1-8z" />,
  check: <path d="M5 12l5 5 9-11" />,
  star: <path d="M12 4l2.4 5 5.6.8-4 4 1 5.6-5-2.7-5 2.7 1-5.6-4-4 5.6-.8z" />,
  bell: <path d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4l2-2zM10 20a2 2 0 0 0 4 0" />,
  database: <><ellipse cx="12" cy="6" rx="7" ry="3" /><path d="M5 6v6c0 1.7 3.1 3 7 3s7-1.3 7-3V6M5 12v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" /></>,
  info: <><circle cx="12" cy="12" r="8" /><path d="M12 11v5M12 8h.01" /></>
};

const BlockIcon: React.FC<{ name?: string }> = ({ name }) => (
  <svg className="etx-td__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICON_PATHS[name ?? ''] ?? ICON_PATHS.star}
  </svg>
);

// ─── Mini-markdown sicuro (nessun HTML raw) ──────────────────────────────────
// Inline: **grassetto**, *corsivo*, `codice`, [testo](url).
const renderInline = (text: string, keyPrefix: string): React.ReactNode[] => {
  const tokens = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g);
  return tokens.filter(Boolean).map((tok, i) => {
    const key = `${keyPrefix}-${i}`;
    if (tok.startsWith('**') && tok.endsWith('**')) return <strong key={key}>{tok.slice(2, -2)}</strong>;
    if (tok.startsWith('*') && tok.endsWith('*')) return <em key={key}>{tok.slice(1, -1)}</em>;
    if (tok.startsWith('`') && tok.endsWith('`')) return <code key={key}>{tok.slice(1, -1)}</code>;
    const link = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const href = link[2];
      const safe = /^(https?:|mailto:|\/)/i.test(href) ? href : '#';
      return <a key={key} href={safe} target="_blank" rel="noopener noreferrer">{link[1]}</a>;
    }
    return <React.Fragment key={key}>{tok}</React.Fragment>;
  });
};

// Blocco: paragrafi, heading (## / ###), liste (- ), separati da riga vuota.
const MiniMarkdown: React.FC<{ source: string }> = ({ source }) => {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const out: React.ReactNode[] = [];
  let paragraph: string[] = [];
  let list: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length) {
      const text = paragraph.join(' ');
      out.push(<p key={`p-${out.length}`}>{renderInline(text, `p${out.length}`)}</p>);
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list.length) {
      out.push(
        <ul key={`ul-${out.length}`}>
          {list.map((item, i) => <li key={i}>{renderInline(item, `li${out.length}-${i}`)}</li>)}
        </ul>
      );
      list = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushParagraph(); flushList(); continue; }
    if (line.startsWith('### ')) { flushParagraph(); flushList(); out.push(<h5 key={`h-${out.length}`}>{renderInline(line.slice(4), `h${out.length}`)}</h5>); continue; }
    if (line.startsWith('## ')) { flushParagraph(); flushList(); out.push(<h4 key={`h-${out.length}`}>{renderInline(line.slice(3), `h${out.length}`)}</h4>); continue; }
    if (line.startsWith('- ') || line.startsWith('* ')) { flushParagraph(); list.push(line.slice(2)); continue; }
    flushList();
    paragraph.push(line);
  }
  flushParagraph();
  flushList();

  return <>{out}</>;
};


// ─── Modifica sull'anteprima ─────────────────────────────────────────────────
// Senza contesto il renderer mostra il contenuto e basta (portale, sito). L'editor
// dell'admin avvolge un blocco in ContentEditContext: i testi diventano scrivibili
// dove sono, e i campi vuoti compaiono come segnaposto.

/** Percorso di un campo dentro il blocco, es. ['items', 2, 'title']. */
export type ContentPath = (string | number)[];
export interface ContentEdit {
  onText: (path: ContentPath, value: string) => void;
}
export const ContentEditContext = createContext<ContentEdit | null>(null);
const useEditing = () => useContext(ContentEditContext) !== null;

/**
 * Un testo del blocco. In modifica è un campo scrivibile: mostra la resa finale finché
 * non lo si tocca, il testo grezzo (con **grassetto** e simili) mentre si scrive, e
 * salva all'uscita. Invio esce dai campi su una riga; Esc annulla.
 */
const Text: React.FC<{ path: ContentPath; value?: string; rich?: boolean; multiline?: boolean; placeholder?: string }> =
  ({ path, value = '', rich, multiline, placeholder = 'Scrivi qui' }) => {
    const edit = useContext(ContentEditContext);
    const ref = useRef<HTMLElement>(null);
    const [active, setActive] = useState(false);
    // Cambia a ogni uscita: React ricrea il nodo invece di riconciliare un DOM modificato a mano.
    const [round, setRound] = useState(0);

    useLayoutEffect(() => {
      const node = ref.current;
      if (!active || !node) return;
      const range = document.createRange();
      range.selectNodeContents(node);
      range.collapse(false);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }, [active]);

    const shown = rich
      ? (multiline ? <MiniMarkdown source={value} /> : renderInline(value, path.join('-')))
      : value;
    if (!edit) return <>{shown}</>;

    const finish = (commit: boolean) => {
      const text = (ref.current?.innerText ?? '').replace(/ /g, ' ').replace(/\n+$/, '');
      setActive(false);
      setRound(r => r + 1);
      if (commit && text !== value) edit.onText(path, multiline ? text : text.replace(/\s*\n\s*/g, ' '));
    };

    // Il testo su più righe contiene paragrafi ed elenchi: serve un contenitore di blocco.
    const Tag = (multiline ? 'div' : 'span') as 'span';
    return (
      <Tag
        key={round}
        ref={ref as React.Ref<HTMLSpanElement>}
        className={`etx-ed${multiline ? ' etx-ed--block' : ''}${value ? '' : ' is-empty'}`}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline={multiline || undefined}
        aria-label={placeholder}
        data-placeholder={placeholder}
        // L'editor mostra la barra di formattazione solo sui campi che accettano il markdown.
        data-rich={rich ? (multiline ? 'block' : 'inline') : undefined}
        spellCheck
        onFocus={() => setActive(true)}
        onBlur={() => finish(true)}
        onKeyDown={e => {
          if (e.key === 'Escape') { e.preventDefault(); finish(false); ref.current?.blur(); }
          if (e.key === 'Enter' && !multiline) { e.preventDefault(); ref.current?.blur(); }
        }}
        onPaste={e => {
          e.preventDefault();
          document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
        }}
      >
        {active ? value : shown}
      </Tag>
    );
  };

/** Un titolo facoltativo: in lettura compare solo se c'è, in modifica sempre. */
const OptionalTitle: React.FC<{ value?: string; placeholder?: string }> = ({ value, placeholder = 'Titolo (facoltativo)' }) => {
  const editing = useEditing();
  return value || editing
    ? <h4 className="etx-td__section-title"><Text path={['title']} value={value} placeholder={placeholder} /></h4>
    : null;
};

// ─── Blocchi ─────────────────────────────────────────────────────────────────
const Hero: React.FC<{ block: HeroBlock }> = ({ block }) => {
  const editing = useEditing();
  return (
    <div className="etx-td__hero">
      {(block.tagline || editing) && (
        <p className="etx-td__tagline"><Text path={['tagline']} value={block.tagline} rich placeholder="Frase di apertura" /></p>
      )}
      {block.highlights && block.highlights.length > 0 && (
        <div className="etx-td__highlights">
          {block.highlights.map((h, i) => (
            <span key={i} className="etx-td__highlight"><Text path={['highlights', i]} value={h} placeholder="Punto in evidenza" /></span>
          ))}
        </div>
      )}
    </div>
  );
};

const FeatureGrid: React.FC<{ block: FeatureGridBlock }> = ({ block }) => {
  const editing = useEditing();
  return (
    <div className="etx-td__section">
      <OptionalTitle value={block.title} />
      <div className="etx-td__features">
        {block.items.map((item, i) => (
          <div key={i} className="etx-td__feature">
            <BlockIcon name={item.icon} />
            <p className="etx-td__feature-title"><Text path={['items', i, 'title']} value={item.title} placeholder="Funzionalità" /></p>
            {(item.text || editing) && (
              <p className="etx-td__feature-text"><Text path={['items', i, 'text']} value={item.text} placeholder="Descrizione" /></p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const Callout: React.FC<{ block: CalloutBlock }> = ({ block }) => {
  const editing = useEditing();
  return (
    <div className={`etx-td__callout etx-td__callout--${block.variant ?? 'info'}`}>
      <BlockIcon name={block.icon ?? (block.variant === 'security' ? 'lock' : 'info')} />
      <div>
        {(block.title || editing) && <p className="etx-td__callout-title"><Text path={['title']} value={block.title} placeholder="Titolo (facoltativo)" /></p>}
        <p className="etx-td__callout-text"><Text path={['text']} value={block.text} rich placeholder="Testo del riquadro" /></p>
      </div>
    </div>
  );
};

const Steps: React.FC<{ block: StepsBlock }> = ({ block }) => {
  const editing = useEditing();
  return (
    <div className="etx-td__section">
      <OptionalTitle value={block.title} />
      <ol className="etx-td__steps">
        {block.items.map((item, i) => (
          <li key={i} className="etx-td__step">
            <span className="etx-td__step-n">{i + 1}</span>
            <div>
              <p className="etx-td__step-title"><Text path={['items', i, 'title']} value={item.title} placeholder="Passo" /></p>
              {(item.text || editing) && <p className="etx-td__step-text"><Text path={['items', i, 'text']} value={item.text} placeholder="Dettagli (facoltativi)" /></p>}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};

const ChipList: React.FC<{ block: ChipListBlock }> = ({ block }) => (
  <div className="etx-td__section">
    <OptionalTitle value={block.title} />
    <div className="etx-td__chips">
      {block.chips.map((chip, i) => <span key={i} className="etx-td__chip"><Text path={['chips', i]} value={chip} placeholder="Pillola" /></span>)}
    </div>
  </div>
);

const Specs: React.FC<{ block: SpecsBlock }> = ({ block }) => (
  <div className="etx-td__section">
    <OptionalTitle value={block.title} />
    <table className="etx-td__specs">
      <tbody>
        {block.rows.map((row, i) => (
          <tr key={i}>
            <th><Text path={['rows', i, 'label']} value={row.label} placeholder="Etichetta" /></th>
            <td><Text path={['rows', i, 'value']} value={row.value} placeholder="Valore" /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Faq: React.FC<{ block: FaqBlock }> = ({ block }) => {
  const editing = useEditing();
  return (
    <div className="etx-td__section">
      <OptionalTitle value={block.title} />
      <div className="etx-td__faq">
        {block.items.map((item, i) => editing ? (
          // In modifica la risposta resta aperta: un <summary> scrivibile aprirebbe e chiuderebbe a ogni clic.
          <div key={i} className="etx-td__faq-item is-open">
            <p className="etx-td__faq-q"><Text path={['items', i, 'q']} value={item.q} placeholder="Domanda" /></p>
            <div className="etx-td__faq-answer"><Text path={['items', i, 'a']} value={item.a} rich placeholder="Risposta" /></div>
          </div>
        ) : (
          <details key={i} className="etx-td__faq-item">
            <summary>{item.q}</summary>
            <div className="etx-td__faq-answer">{renderInline(item.a, `faq${i}`)}</div>
          </details>
        ))}
      </div>
    </div>
  );
};

const Media: React.FC<{ block: MediaBlock }> = ({ block }) => {
  const editing = useEditing();
  if (!isImageUrl(block.url)) {
    return editing ? <p className="etx-td__placeholder">Immagine: indica l’indirizzo nel pannello a destra.</p> : null;
  }
  return (
    <figure className="etx-td__media">
      <img src={resolveImageUrl(block.url)} alt={block.alt ?? ''} loading="lazy" />
      {(block.caption || editing) && <figcaption><Text path={['caption']} value={block.caption} placeholder="Didascalia (facoltativa)" /></figcaption>}
    </figure>
  );
};

const Gallery: React.FC<{ block: GalleryBlock }> = ({ block }) => {
  const editing = useEditing();
  const images = block.images.map((image, index) => ({ image, index })).filter(({ image }) => isImageUrl(image.url));
  if (!images.length) {
    return editing ? <p className="etx-td__placeholder">Galleria: aggiungi le schermate nel pannello a destra.</p> : null;
  }
  return (
    <div className="etx-td__section">
      <OptionalTitle value={block.title} />
      <div className="etx-td__gallery">
        {images.map(({ image, index }) => (
          <figure key={index} className="etx-td__media">
            <img src={resolveImageUrl(image.url)} alt={image.alt ?? ''} loading="lazy" />
            {(image.caption || editing) && <figcaption><Text path={['images', index, 'caption']} value={image.caption} placeholder="Didascalia (facoltativa)" /></figcaption>}
          </figure>
        ))}
      </div>
    </div>
  );
};

const Cta: React.FC<{ block: CtaBlock; href?: string }> = ({ block, href }) => {
  const editing = useEditing();
  const label = block.label?.trim() || 'Entra in Semplora';
  return (
    <div className="etx-td__cta">
      <div>
        <p className="etx-td__cta-title"><Text path={['title']} value={block.title} placeholder="Titolo dell’invito" /></p>
        {(block.text || editing) && <p className="etx-td__cta-text"><Text path={['text']} value={block.text} rich placeholder="Testo (facoltativo)" /></p>}
      </div>
      {href && !editing
        ? <a className="etx-td__cta-button" href={href}>{label}</a>
        : <span className="etx-td__cta-button">{editing ? <Text path={['label']} value={block.label} placeholder="Entra in Semplora" /> : label}</span>}
    </div>
  );
};

/** Tool collegati: l'elenco arriva dalla pagina che ospita il renderer (stessa categoria). */
const Related: React.FC<{ block: RelatedBlock; tools?: RelatedTool[] }> = ({ block, tools }) => {
  const editing = useEditing();
  return (
  <div className="etx-td__section">
    <h4 className="etx-td__section-title">
      {editing
        ? <Text path={['title']} value={block.title} placeholder="Altri tool che potrebbero servirti" />
        : block.title?.trim() || 'Altri tool che potrebbero servirti'}
    </h4>
    {tools && tools.length > 0 ? (
      <ul className="etx-td__related">
        {tools.map(tool => (
          <li key={tool.code}>
            {tool.href ? <a href={tool.href}>{tool.name}</a> : <strong>{tool.name}</strong>}
            {tool.description && <span>{tool.description}</span>}
          </li>
        ))}
      </ul>
    ) : (
      <p className="etx-td__placeholder">Qui compaiono gli altri tool pubblicati della stessa categoria.</p>
    )}
  </div>
  );
};

const Table: React.FC<{ block: TableBlock }> = ({ block }) => {
  const editing = useEditing();
  return (
    <div className="etx-td__section">
      <OptionalTitle value={block.title} />
      <div className="etx-td__table-wrap">
        <table className="etx-td__table">
          {(editing || block.head.some(cell => cell.trim())) && (
            <thead><tr>{block.head.map((cell, i) => <th key={i}><Text path={['head', i]} value={cell} placeholder={`Colonna ${i + 1}`} /></th>)}</tr></thead>
          )}
          <tbody>
            {block.rows.map((row, r) => (
              <tr key={r}>{row.map((cell, c) => <td key={c}><Text path={['rows', r, c]} value={cell} rich placeholder="…" /></td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Blocchi da builder ──────────────────────────────────────────────────────
// Link ammessi nei blocchi: pagine https, email, percorsi del sito e ancore della pagina (#…).
const safeHref = (url?: string) => (url && /^(https?:|mailto:|\/|#[a-z0-9])/i.test(url.trim()) ? url.trim() : undefined);
const isExternal = (href: string) => /^https?:/i.test(href);

/** Solo YouTube e Vimeo, in modalità che non traccia: un indirizzo diverso non diventa un riquadro. */
export function videoEmbedUrl(url: string): string | null {
  const value = url.trim();
  const youtube = value.match(/^https?:\/\/(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/i);
  if (youtube) return `https://www.youtube-nocookie.com/embed/${youtube[1]}`;
  const vimeo = value.match(/^https?:\/\/(?:www\.)?(?:player\.)?vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}?dnt=1`;
  return null;
}

const Heading: React.FC<{ block: HeadingBlock }> = ({ block }) => {
  const editing = useEditing();
  const Tag = block.level === 3 ? 'h4' : 'h3';
  return (
    <div className={`etx-td__heading etx-td__heading--${block.level === 3 ? 'small' : 'large'}`}>
      {(block.eyebrow || editing) && <p className="etx-td__eyebrow"><Text path={['eyebrow']} value={block.eyebrow} placeholder="Riga sopra (facoltativa)" /></p>}
      <Tag className="etx-td__heading-text"><Text path={['text']} value={block.text} placeholder="Titolo" /></Tag>
      {(block.subtitle || editing) && <p className="etx-td__heading-sub"><Text path={['subtitle']} value={block.subtitle} rich placeholder="Sottotitolo (facoltativo)" /></p>}
    </div>
  );
};

const Button: React.FC<{ block: ButtonBlock }> = ({ block }) => {
  const editing = useEditing();
  const href = safeHref(block.url);
  const className = `etx-td__button etx-td__button--${block.style ?? 'primary'}`;
  const label = editing ? <Text path={['label']} value={block.label} placeholder="Testo del pulsante" /> : block.label;
  return (
    <div className="etx-td__button-row">
      {href && !editing
        ? <a className={className} href={href} {...(isExternal(href) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{label}</a>
        : <span className={className}>{label}</span>}
    </div>
  );
};

const MediaText: React.FC<{ block: MediaTextBlock }> = ({ block }) => {
  const editing = useEditing();
  const image = isImageUrl(block.url);
  return (
    <div className={`etx-td__mediatext etx-td__mediatext--${block.imageSide === 'left' ? 'left' : 'right'}`}>
      <div className="etx-td__mediatext-text">
        {(block.title || editing) && <h4 className="etx-td__section-title"><Text path={['title']} value={block.title} placeholder="Titolo (facoltativo)" /></h4>}
        <div className="etx-td__richtext">
          <Text path={['markdown']} value={block.markdown} rich multiline placeholder="Testo accanto all'immagine" />
        </div>
      </div>
      <div className="etx-td__mediatext-media">
        {image
          ? <img src={resolveImageUrl(block.url)} alt={block.alt ?? ''} loading="lazy" />
          : editing && <p className="etx-td__placeholder">Indica l’indirizzo dell’immagine nel pannello a destra.</p>}
      </div>
    </div>
  );
};

const Quote: React.FC<{ block: QuoteBlock }> = ({ block }) => {
  const editing = useEditing();
  return (
    <figure className="etx-td__quote">
      <blockquote><Text path={['text']} value={block.text} rich placeholder="La citazione" /></blockquote>
      {(block.author || block.role || editing) && (
        <figcaption>
          <strong><Text path={['author']} value={block.author} placeholder="Chi lo dice" /></strong>
          {(block.role || editing) && <span><Text path={['role']} value={block.role} placeholder="Ruolo e azienda" /></span>}
        </figcaption>
      )}
    </figure>
  );
};

const Stats: React.FC<{ block: StatsBlock }> = ({ block }) => (
  <div className="etx-td__section">
    <OptionalTitle value={block.title} />
    <div className="etx-td__stats">
      {block.items.map((item, i) => (
        <div key={i} className="etx-td__stat">
          <strong><Text path={['items', i, 'value']} value={item.value} placeholder="80%" /></strong>
          <span><Text path={['items', i, 'label']} value={item.label} placeholder="Cosa misura" /></span>
        </div>
      ))}
    </div>
  </div>
);

const Cards: React.FC<{ block: CardsBlock }> = ({ block }) => {
  const editing = useEditing();
  return (
    <div className="etx-td__section">
      <OptionalTitle value={block.title} />
      <div className="etx-td__cards">
        {block.items.map((item, i) => {
          const href = safeHref(item.url);
          return (
            <div key={i} className="etx-td__card">
              {item.image && isImageUrl(item.image)
                ? <img className="etx-td__card-image" src={resolveImageUrl(item.image)} alt="" loading="lazy" />
                : item.icon && <BlockIcon name={item.icon} />}
              <p className="etx-td__card-title"><Text path={['items', i, 'title']} value={item.title} placeholder="Titolo della scheda" /></p>
              {(item.text || editing) && <p className="etx-td__card-text"><Text path={['items', i, 'text']} value={item.text} rich placeholder="Testo (facoltativo)" /></p>}
              {href && (editing
                ? <span className="etx-td__card-link"><Text path={['items', i, 'linkLabel']} value={item.linkLabel} placeholder="Scopri di più" /></span>
                : <a className="etx-td__card-link" href={href} {...(isExternal(href) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{item.linkLabel?.trim() || 'Scopri di più'} →</a>)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Video: React.FC<{ block: VideoBlock }> = ({ block }) => {
  const editing = useEditing();
  const embed = videoEmbedUrl(block.url);
  if (!embed) {
    return editing ? <p className="etx-td__placeholder">Video: incolla nel pannello a destra un indirizzo di YouTube o Vimeo.</p> : null;
  }
  return (
    <figure className="etx-td__video">
      <div className="etx-td__video-frame">
        {/* In modifica il riquadro non prende i clic, così il blocco resta selezionabile. */}
        <iframe src={embed} title={block.caption || 'Video'} loading="lazy" style={editing ? { pointerEvents: 'none' } : undefined}
          allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen" referrerPolicy="strict-origin-when-cross-origin" />
      </div>
      {(block.caption || editing) && <figcaption><Text path={['caption']} value={block.caption} placeholder="Didascalia (facoltativa)" /></figcaption>}
    </figure>
  );
};

const Divider: React.FC<{ block: DividerBlock }> = ({ block }) => (
  <div className={`etx-td__divider etx-td__divider--${block.size ?? 'medium'}`} role="separator">
    {block.style !== 'space' && <hr />}
  </div>
);

const Logos: React.FC<{ block: LogosBlock }> = ({ block }) => {
  const editing = useEditing();
  const items = block.items.filter(item => isImageUrl(item.url));
  return (
    <div className="etx-td__section">
      <OptionalTitle value={block.title} placeholder="Chi lo usa (facoltativo)" />
      {items.length ? (
        <ul className="etx-td__logos">
          {items.map((item, i) => {
            const href = safeHref(item.href);
            const img = <img src={resolveImageUrl(item.url)} alt={item.alt} loading="lazy" />;
            return <li key={i}>{href && !editing ? <a href={href} target="_blank" rel="noopener noreferrer">{img}</a> : img}</li>;
          })}
        </ul>
      ) : editing && <p className="etx-td__placeholder">Loghi: aggiungi le immagini nel pannello a destra.</p>}
    </div>
  );
};

export interface RelatedTool {
  code: string;
  name: string;
  description?: string;
  href?: string;
}

interface RenderOptions {
  ctaHref?: string;
  related?: RelatedTool[];
}

const renderBlock = (block: ToolDetailBlock, index: number, options: RenderOptions): React.ReactNode => {
  switch (block.type) {
    case 'heading': return <Heading key={index} block={block} />;
    case 'button': return <Button key={index} block={block} />;
    case 'mediaText': return <MediaText key={index} block={block} />;
    case 'quote': return <Quote key={index} block={block} />;
    case 'stats': return <Stats key={index} block={block} />;
    case 'cards': return <Cards key={index} block={block} />;
    case 'video': return <Video key={index} block={block} />;
    case 'divider': return <Divider key={index} block={block} />;
    case 'logos': return <Logos key={index} block={block} />;
    case 'hero': return <Hero key={index} block={block} />;
    case 'richText': return (
      <div key={index} className="etx-td__richtext">
        <Text path={['markdown']} value={(block as RichTextBlock).markdown} rich multiline placeholder="Scrivi il testo. ## per un titolo, - per un elenco" />
      </div>
    );
    case 'featureGrid': return <FeatureGrid key={index} block={block} />;
    case 'callout': return <Callout key={index} block={block} />;
    case 'steps': return <Steps key={index} block={block} />;
    case 'chipList': return <ChipList key={index} block={block} />;
    case 'specs': return <Specs key={index} block={block} />;
    case 'faq': return <Faq key={index} block={block} />;
    case 'media': return <Media key={index} block={block} />;
    case 'gallery': return <Gallery key={index} block={block} />;
    case 'cta': return <Cta key={index} block={block} href={options.ctaHref} />;
    case 'related': return <Related key={index} block={block} tools={options.related} />;
    case 'section': return <h3 key={index} className="etx-td__guide-section"><Text path={['title']} value={block.title} placeholder="Titolo del capitolo" /></h3>;
    case 'table': return <Table key={index} block={block} />;
    case 'pageBreak': return <hr key={index} className="etx-td__page-break" aria-label="Interruzione di pagina" />;
    default: return null;
  }
};

/** Un solo blocco, per le anteprime che avvolgono ogni blocco (editor dell'admin). */
export const ToolDetailBlockView: React.FC<{ block: ToolDetailBlock } & RenderOptions> = ({ block, ...options }) =>
  <>{renderBlock(block, 0, options)}</>;

/** Ancora valida: lettere minuscole, cifre e trattini. */
export const cleanAnchor = (value?: string) =>
  (value ?? '').trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);

/** Classi e ancora del contenitore di un blocco, dal suo stile (block.appearance). */
export function blockFrameProps(block: ToolDetailBlock): { className: string; id?: string } {
  const a = block.appearance ?? {};
  const classes = [
    a.spaceTop && `etx-block--space-top-${a.spaceTop}`,
    a.spaceBottom && `etx-block--space-bottom-${a.spaceBottom}`,
    a.align && `etx-block--align-${a.align}`,
    a.accent && `etx-block--accent-${a.accent}`,
    a.hideOn && `etx-block--hide-${a.hideOn}`,
  ].filter(Boolean);
  const anchor = cleanAnchor(a.anchor);
  return { className: ['etx-block', ...classes].join(' '), ...(anchor ? { id: anchor } : {}) };
}

/** Classi di una sezione: sfondo, larghezza, colonne e allineamento. */
export const sectionClassName = (section: Omit<ContentSection, 'blocks'>) => [
  'etx-doc__section',
  `etx-doc__section--bg-${section.background ?? 'plain'}`,
  `etx-doc__section--w-${section.width ?? 'normal'}`,
  `etx-doc__section--${section.layout ?? 'stack'}`,
  `etx-doc__section--align-${section.align ?? 'start'}`,
].join(' ');

/** Cornice di una sezione con il suo stile: la usa il renderer e, con i blocchi modificabili, l'editor. */
export const ContentSectionFrame: React.FC<{ section: Omit<ContentSection, 'blocks'>; className?: string; children: React.ReactNode }> =
  ({ section, className, children }) => (
    <section className={`${sectionClassName(section)}${className ? ` ${className}` : ''}`}>
      <div className="etx-doc__inner">{children}</div>
    </section>
  );

/**
 * Renderizza un documento a sezioni (vedi document.ts). Presentazionale e autonomo:
 * nessuna dipendenza dal framework dell'app ospite, così è identico nel portale, sul
 * sito e nell'anteprima dell'admin. Senza ctaHref il pulsante dell'invito
 * all'azione non è un link (anteprima).
 */
export const ToolContentView: React.FC<{ document: ToolContentDocument } & RenderOptions> = ({ document: doc, ...options }) => {
  const sections = doc.sections.filter(section => section.blocks.length);
  if (!sections.length) {
    return <p className="etx-td__empty">Nessuna descrizione approfondita disponibile.</p>;
  }
  return (
    <div className="etx-td etx-doc">
      {sections.map((section, s) => (
        <ContentSectionFrame key={s} section={section}>
          {section.blocks.map((block, index) => {
            const frame = blockFrameProps(block);
            return <div key={index} {...frame} className={`etx-doc__block ${frame.className}`}>{renderBlock(block, index, options)}</div>;
          })}
        </ContentSectionFrame>
      ))}
    </div>
  );
};

/** Formato 1: un elenco di blocchi senza sezioni. */
const ToolDetailRenderer: React.FC<{ blocks: ToolDetailBlock[] } & RenderOptions> = ({ blocks, ...options }) => {
  if (!blocks.length) {
    return <p className="etx-td__empty">Nessuna descrizione approfondita disponibile.</p>;
  }
  return <div className="etx-td">{blocks.map((block, index) => renderBlock(block, index, options))}</div>;
};

export default ToolDetailRenderer;
